import React, {useEffect, useState} from 'react'
import {
  useMediaQuery,
  ThemeProvider,
  MenuItem,
  Box,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ButtonBase,
  Menu,
  Container
} from '@mui/material'
import {fonts, theme} from '../../../config/Dashboardtheme'
import Pagination from '@mui/material/Pagination'
import MpSvgIcon from './MpSvgIcon'
import {useAppSelector} from '../../../redux/storeExports'
import ConfirmationModal from './ConfirmationModal'
import ExecutiveUserStatsModal from './ExecutiveUserStatsModal'
import {ExecutiveEmployeesSelector, ExecutiveCorporateEmployee} from '../../../redux/corporateService'
import {ExecutiveUserProfileDashboard} from './ExecutiveUserProfileDashboard'
import Deleteicon from '@meprism/shared/src/assets/icons/Delete.svg'
import PersonIcon from '@mui/icons-material/Person';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';

interface propsType {
  searchValue: string
}

function useDebounce(value: string, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const ExecutiveUserManagementListItem = (prop: propsType) => {

  const executiveEmployees = useAppSelector(ExecutiveEmployeesSelector)
  const [open, setOpen] = useState(false)
  const [statsOpen, setStatsOpen] = useState(false)
  const [profileDashboardOpen, setProfileDashboardOpen] = useState(false)
  const debouncedSearchValue = useDebounce(prop.searchValue, 500);

  // Sorting state
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [sortBy, setSortBy] = useState<string>('col1');

  const filteredEmployees = executiveEmployees.filter((employee: ExecutiveCorporateEmployee) => {
    const searchValue = debouncedSearchValue.toLowerCase();
    return (
      employee.name.toLowerCase().includes(searchValue) ||
      employee.email.toLowerCase().includes(searchValue)
    );
  });

  const rows = filteredEmployees
    .map((item: ExecutiveCorporateEmployee, index: number) => ({
      id: index + 1,
      col1: item.name,
      col2: item.email,
      col3: item.muid,
    }))

  // Sorting handler
  const handleSort = (columnKey: string) => {
    if (sortBy === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(columnKey);
      setSortDirection('asc');
    }
  };

  // Sort rows before rendering
  const sortedRows = [...rows].sort((a, b) => {
    if (sortBy === 'col1') {
      const nameA = a.col1.toLowerCase();
      const nameB = b.col1.toLowerCase();
      if (nameA < nameB) return sortDirection === 'asc' ? -1 : 1;
      if (nameA > nameB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    }
    return 0;
  });

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [page, setPage] = useState(0)
  const rowsPerPage = 10
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage - 1)
  }
  useEffect(() => {
    const totalPages = Math.ceil(filteredEmployees.length / rowsPerPage);
    if (page >= totalPages && page !== 0) {
      setPage(totalPages - 1);
    }
  }, [filteredEmployees.length, page, rowsPerPage]);
  
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null)
  const isOpen = Boolean(anchorEl)
  const buttonRef = React.useRef<HTMLButtonElement>(null)
  const [openMenuIndex, setOpenMenuIndex] = React.useState<number | null>()
  const [muidrow, setMuidRow] = React.useState<string | null>()
  const handleButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (openMenuIndex === index) {
      setOpenMenuIndex(null);
      setAnchorEl(null);
    } else {
      setOpenMenuIndex(index);
      setAnchorEl(event.currentTarget);
      const clickedRow = rows.find((row) => row.id === index);
      setMuidRow(clickedRow?.col3 || null);
    }
  };

  const handleIconClick = (
    index: number,
  ) => {
    if (openMenuIndex === index) {
      setOpenMenuIndex(null);
      setAnchorEl(null);
    } else {
      setOpenMenuIndex(index);
      const clickedRow = rows.find((row) => row.id === index);
      setMuidRow(clickedRow?.col3 || null);
    }
  };

  const close = () => {
    setAnchorEl(null)
    setOpenMenuIndex(null)
    buttonRef.current?.focus()
  }
  const handleopen = () => {
    setOpen(true)
  }

  // Set consistent widths for all columns
  const columns = [
    {key: 'col1', name: 'Name', width: '35%'},
    {key: 'col2', name: 'Email', width: '55%'},
    {
      key: 'col3',
      name: 'Actions',
      width: '10%',
      formatter: (props: any) => (
        <Box key={props.row.col3}>
          <Container sx={{display: 'flex', width: '100%', justifyContent: 'space-between'}} style={{padding: '0'}}>
            <ButtonBase title='User Profile Management' onClick={() => {
              handleIconClick(props.row.id)
              setProfileDashboardOpen(true)
            }}>
              <PersonIcon fontSize='small' color='info' />
            </ButtonBase>
            <ButtonBase title='Broker Stats' onClick={() => {
              handleIconClick(props.row.id)
              setStatsOpen(true)
            }}>
              <LeaderboardIcon fontSize='small' />
            </ButtonBase>
            <ButtonBase title='Remove Employee' onClick={() => {
              handleIconClick(props.row.id)
              handleopen()
            }}>
              <img src={Deleteicon} style={{width: '15px'}} />
            </ButtonBase>
          </Container>
        </Box>
      ),
    },
  ]

  return (
    <ThemeProvider theme={theme}>
      {!isMobile ? (
        <Box
          style={{height: 'auto', width: '100%', paddingBottom: '16px'}}
          bgcolor="#fff"
          borderRadius={5}
          p={3}>
          <TableContainer>
            <Table
              style={{
                background: '#fff',
                color: '#040115',
                borderRadius: 5,
                padding: 3,
              }}>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.key}
                      style={{
                        width: column.width,
                        minWidth: column.width,
                        maxWidth: column.width,
                        borderBottom: '1.5px solid #426BF0',
                        ...fonts.Inter.SemiBold,
                        cursor: column.key === 'col1' ? 'pointer' : undefined,
                        userSelect: column.key === 'col1' ? 'none' : undefined,
                      }}
                      onClick={column.key === 'col1' ? () => handleSort('col1') : undefined}
                    >
                      {column.name}
                      {column.key === 'col1' && (sortBy === 'col1' ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : '')}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {sortedRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow key={row.id}>
                      {columns.map((column) => (
                        <TableCell
                          key={column.key}
                          style={{
                            borderBottom: '1.5px solid #615F6B',
                            width: column.width,
                            minWidth: column.width,
                            maxWidth: column.width,
                          }}>
                          {column.formatter ? (
                            column.formatter({row})
                          ) : (
                            row[column.key as keyof typeof row]
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 2,
                padding: 2,
                borderRadius: 5,
                background: '#fff',
              }}>
              <Pagination
                variant="outlined"
                shape="rounded"
                count={Math.ceil(rows.length / rowsPerPage)}
                page={page + 1}
                onChange={handleChangePage}
              />
            </Box>
          </TableContainer>
          <ConfirmationModal
            open={open}
            setOpen={setOpen}
            muid={muidrow}
            isExecutive={true}
          />
          <ExecutiveUserStatsModal
            open={statsOpen}
            setOpen={setStatsOpen}
            muid={muidrow}
          />
          <ExecutiveUserProfileDashboard
            isModalOpen={profileDashboardOpen}
            muid={muidrow ? muidrow : ''}
            setIsModalOpen={setProfileDashboardOpen}
          />
        </Box>
      ) : (
        <Box
          style={{height: 'auto', width: '100%'}}
          bgcolor="#fff"
          borderRadius={5}
          p={3}>
          {rows.map((row) => (
            <Grid
              key={row.id}
              container
              direction="column"
              spacing={2}
              sx={{
                borderBottom: '1px solid rgba(97, 95, 107, 0.5)',
                padding: 2,
                position: 'relative',
              }}>
              <Box
                sx={{
                  backgroundColor:
                    openMenuIndex === row.id ? '#ECF0FD' : 'white',
                  borderRadius: '2px',
                  position: 'absolute',
                  top: '0',
                  mt: 5,
                  right: '0',
                }}
                key={row.id}>
                <ButtonBase
                  sx={{borderRadius: '10px'}}
                  type="button"
                  onClick={(event) => handleButtonClick(event, row.id)}
                  ref={buttonRef}
                  aria-controls={isOpen ? 'simple-menu' : undefined}
                  aria-expanded={isOpen || undefined}
                  aria-haspopup="menu">
                  <MpSvgIcon
                    icon={
                      openMenuIndex === row.id ? 'threeDotsBlue' : 'threeDotsNew'
                    }
                  />
                </ButtonBase>
                <Menu
                  open={openMenuIndex === row.id}
                  onClose={close}
                  anchorEl={anchorEl}
                  PaperProps={{
                    style: {
                      backgroundColor: 'rgba(111, 142, 245, 0.10)',
                    },
                  }}
                  sx={{borderRadius: '20px', opacity: 1}}>
                  <MenuItem
                    onClick={() => {
                      handleopen()
                      close()
                    }}>
                    <Typography variant={'label'} color={'#1A1B20'}>
                      Remove Employee
                    </Typography>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setProfileDashboardOpen(true)
                    }}>
                    <Typography variant={'label'} color={'#1A1B20'}>
                      Profile Dashboard
                    </Typography>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setStatsOpen(true)
                    }}>
                    <Typography variant={'label'} color={'#1A1B20'}>
                      View Removal Stats
                    </Typography>
                  </MenuItem>
                </Menu>
              </Box>
              {columns
                .filter((col) => col.key !== 'col3')
                .map((col) => (
                  <React.Fragment key={col.key}>
                    <Grid item xs={6}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'left',
                          wordWrap: 'break-word',
                        }}>
                        <Typography variant="h5">{col.name}</Typography>
                        <Typography variant="label">
                          {
                            row[col.key as keyof typeof row]
                          }
                        </Typography>
                      </Box>
                    </Grid>
                  </React.Fragment>
                ))}
            </Grid>
          ))}
          <ConfirmationModal
            open={open}
            setOpen={setOpen}
            muid={muidrow}
            isExecutive={true}
          />
          <ExecutiveUserStatsModal
            open={statsOpen}
            setOpen={setStatsOpen}
            muid={muidrow}
          />
          <ExecutiveUserProfileDashboard
            isModalOpen={profileDashboardOpen}
            muid={muidrow ? muidrow : ''}
            setIsModalOpen={setProfileDashboardOpen}
          />
        </Box>
      )}
    </ThemeProvider>
  )
}

export default ExecutiveUserManagementListItem